import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, OnInit, output } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { NumericInputDirectiveModule } from '../../../../core/directives/numeric-input.directive';
import { SelectAllOnFocusDirective } from '../../../../core/directives/select-all-on-focus.directive';
import { DialogService } from '../../../../core/services/dialog.service';
import { System } from '../../core';
import { TakeOffService } from '../../takeoff.service';
import { ApplyScaleTo, ApplyScaleToOptions, ManualScaleService, ScalePlanStep } from './manual-scale.service';

@Component({
    selector: 'app-manual-scale',
    imports: [CommonModule, SelectAllOnFocusDirective, NumericInputDirectiveModule, FormsModule],
    templateUrl: './manual-scale.component.html',
    styleUrls: ['./manual-scale.component.scss', '../../../../shared/composable-styles/input.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ManualScaleComponent implements OnInit {
    readonly #takeOffService = inject(TakeOffService);
    readonly #manualScaleService = inject(ManualScaleService);
    readonly #dialogService = inject(DialogService);

    onClose = output<void>();

    takeOffConfig = this.#manualScaleService.takeOffConfig;
    rescale = this.#manualScaleService.rescale;
    plansLength = this.#manualScaleService.plansLength;
    scaleStep = this.#manualScaleService.scaleStep;

    isImperial = this.#takeOffService.getSystem() === System.imperial;
    scaleUnit = this.#takeOffService.getDefaultScaleUnit();

    supportedUnits = this.#takeOffService.getSupportedScaleUnits();

    horizontalLength = this.#manualScaleService.horizontalLength;
    verticalLength = this.#manualScaleService.verticalLength;

    ngOnInit() {
        this.#manualScaleService.scaleUnit.set(this.scaleUnit);
    }

    undoDrawingAndCancel(step: ScalePlanStep) {
        this.changeStep(step);
        this.#manualScaleService.reset();
    }

    changeStep(to: ScalePlanStep, apply = true) {
        this.#manualScaleService.changeStep(to, apply);
    }

    scaleUnitChanged() {
        this.#manualScaleService.scaleUnit.set(this.scaleUnit);
    }

    submitHorizontalScale() {
        this.#manualScaleService.submitHorizontalScale();
    }

    submitVerticalScale(applyTo: ApplyScaleTo) {
        if (applyTo === ApplyScaleToOptions.ALL) {
            this.#dialogService
                .show({
                    title: 'Apply scale to all plans',
                    text: 'This will apply the scale to <strong>all plans in your estimate</strong>. Are you sure you want to proceed?',
                    confirmButtonId: 'btn-apply-scale-change',
                    okText: 'Apply scale',
                    allowHtml: true,
                })
                .then(() => {
                    this.#manualScaleService.submitVerticalScale(applyTo);
                });
        } else this.#manualScaleService.submitVerticalScale(applyTo);
    }

    resetScaleStep() {
        this.#manualScaleService.resetScaleStep();
    }

    onVerticalLengthChange(value: number) {
        this.verticalLength.set(value);
    }

    onHorizontalLengthChange(value: number) {
        this.horizontalLength.set(value);
    }

    close() {
        this.onClose.emit();
    }
}
